import { defineStore } from "pinia";

import { useLayoutStore } from "@/stores/layout";
import serverHttp from "@/utils/http";

export const useFlowStore = defineStore("flow", {
  state: () => ({
    request: {},
    flow: null,
    flow_run: null,
    trigger_run: null,
    previousRequestInstructions: "",
    isFetchingRequestSummary: false,
    isGeneratingFlow: false,
    isFetchingFlow: false,
    setOwnSummary: false, // REMOVE THIS WITH NEW FLOW REQUEST
    flowsForTable: [],
    editMode: false,
  }),
  actions: {
    async createRequest(instructions) {
      const requestBody = {
        request_instructions: instructions,
      };
      try {
        const response = await serverHttp.post("/flow_requests/", requestBody);
        this.request = response.data;
        this.previousRequestInstructions = this.request.request_instructions; // Sync with the server's value
      } catch (error) {
        console.error("Failed to create request:", error);
        throw error;
      }
    },
    async updateRequest() {
      if (!this.request?.id) return;
      if (
        this.request.request_instructions !== this.previousRequestInstructions
      ) {
        try {
          const response = await serverHttp.put(
            "/flow_requests/",
            this.request,
          );
          this.request = response.data;
          this.previousRequestInstructions = this.request.request_instructions;
        } catch (error) {
          console.error("Failed to update request:", error);
          throw error;
        }
      }
    },
    async fetchSummary() {
      if (!this.request.request_instructions) return;
      if (this.setOwnSummary) return;
      this.isFetchingRequestSummary = true;
      try {
        const response = await serverHttp.post(
          `/flow_requests/summarize?id=${this.request.id}`,
        );
        if (response.data) {
          this.request = response.data;
          this.previousRequestInstructions = this.request.request_instructions;
          const layoutStore = useLayoutStore();
          layoutStore.setGenerativeTopbarTitle(this.request.summary);
        } else {
          console.error("Response data is undefined");
        }
      } catch (error) {
        console.error("Failed to fetch summary:", error);
        throw error;
      } finally {
        this.isFetchingRequestSummary = false;
      }
    },
    async generateFlow() {
      if (!this.request.id) return;
      this.isGeneratingFlow = true;
      try {
        const queryParam = new URLSearchParams({
          flow_request_id: this.request.id,
        });
        const response = await serverHttp.post(`/flows/generate?${queryParam}`);
        this.flow = response.data;
      } catch (error) {
        console.error("Failed to generate flow:", error);
        throw error;
      } finally {
        this.isGeneratingFlow = false;
      }
    },
    async fetchFlowTableData() {
      try {
        const response = await serverHttp.get(`/flows/table`);
        this.flowsForTable = response.data;
      } catch (error) {
        console.error("Failed to fetch table data:", error);
        throw error;
      }
    },
    async fetchFlowDetail(flowId) {
      this.isFetchingFlow = true;
      try {
        const params = new URLSearchParams({ id: flowId });

        const response = await serverHttp.get(`/flows/detail`, {
          params,
        });

        this.flow = response.data;
        this.request = this.flow.belongs_to_flow_request;

        if (this.flow.flow_runs && this.flow.flow_runs.length > 0) {
          // Find the flow_run with the highest created_date
          this.flow_run = this.flow.flow_runs.reduce(
            (latestRun, currentRun) => {
              return new Date(currentRun.created_date) >
                new Date(latestRun.created_date)
                ? currentRun
                : latestRun;
            },
          );
        } else {
          this.flow_run = null;
        }

        if (this.flow.trigger_run) {
          this.trigger_run = this.flow.trigger_run;
        } else {
          this.trigger_run = null;
        }
      } catch (error) {
        console.error("Failed to fetch flow:", error);
        throw error;
      } finally {
        this.isFetchingFlow = false;
      }
    },
    async updateFlow() {
      if (!this.flow.id) return;
      try {
        const response = await serverHttp.put("/flows/", this.flow);
        this.flow = response.data;
      } catch (error) {
        console.error("Failed to update flow:", error);
        throw error;
      }
    },
    async deleteFlow(flowId) {
      try {
        await serverHttp.delete(`/flows/?id=${flowId}`);
        if (this.flow && this.flow.id === flowId) {
          this.clearFlow();
        }
      } catch (error) {
        console.error("Failed to delete flow:", error);
        throw error;
      }
    },
    async addTaskOperation(taskOperation) {
      if (!this.flow) return;
      // check if index is already used
      const usedIndices = this.flow.task_operations.map((task) => task.index);
      if (usedIndices.includes(taskOperation.index)) {
        throw new Error("Index already used");
      }
      this.flow.task_operations.push(taskOperation);

      // Flush to the server
      this.updateFlow();
    },
    async removeTaskOperation(taskOperation) {
      // Remove all related dependencies, either if the task operation is the source or target
      this.flow.dependencies = this.flow.dependencies.filter(
        (dep) =>
          dep.source_task_operation !== taskOperation.index &&
          dep.target_task_operation !== taskOperation.index,
      );
      // Remove task operation
      this.flow.task_operations = this.flow.task_operations.filter(
        (task) => task.index !== taskOperation.index,
      );

      // Flush to the server
      this.updateFlow();
    },
    async addDependency(dependency) {
      if (!this.flow) return;

      // Check if the dependency already exists
      // Should be handled by drawflow canvas already but just in case
      const existingDependency = this.flow.dependencies.find(
        (dep) =>
          dep.source_task_operation === dependency.source_task_operation &&
          dep.target_task_operation === dependency.target_task_operation,
      );
      if (existingDependency) {
        throw new Error("Dependency already exists");
      }

      // Check if dependency creates a cycle
      if (this.doesDependencyCreateCycle(dependency)) {
        throw new Error("Adding this dependency would create a cycle");
      }

      this.flow.dependencies.push(dependency);

      // Flush to the server
      this.updateFlow();
    },
    doesDependencyCreateCycle(dependency) {
      const visited = new Set();
      const recStack = new Set();

      const dfs = (taskIndex) => {
        if (recStack.has(taskIndex)) {
          return true; // cycle detected
        }
        if (visited.has(taskIndex)) {
          return false; // already processed without finding a cycle
        }

        visited.add(taskIndex);
        recStack.add(taskIndex);

        const outgoingEdges = this.flow.dependencies.filter(
          (dep) => dep.source_task_operation === taskIndex,
        );

        for (const edge of outgoingEdges) {
          if (dfs(edge.target_task_operation)) {
            return true;
          }
        }
        recStack.delete(taskIndex);
        return false;
      };

      // Temporarily add the dependency to check for cycles
      this.flow.dependencies.push(dependency);
      const hasCycle = dfs(dependency.source_task_operation);
      this.flow.dependencies.pop(); // Remove the temporary dependency

      return hasCycle;
    },
    async removeDependency(
      source_task_operation_index,
      target_task_operation_index,
    ) {
      if (!this.flow) return;

      // Filter out the dependency

      this.flow.dependencies = this.flow.dependencies.filter(
        (dep) =>
          dep.source_task_operation !== source_task_operation_index ||
          dep.target_task_operation !== target_task_operation_index,
      );

      // Flush to the server
      this.updateFlow();
    },
    async setFlowRequestSummary(summary) {
      if (!this.request) return;
      this.request.summary = summary;
      this.setOwnSummary = true;

      const layoutStore = useLayoutStore();
      layoutStore.setTopbarTitle(summary);

      await this.updateRequest();
    },
    async runFlow() {
      if (!this.flow) return;
      if (!this.request) return;

      const params = new URLSearchParams({
        flow_id: this.flow.id,
        flow_request_id: this.request.id,
      });

      try {
        const response = await serverHttp.post(`/flows/execute?${params}`);
        this.flow_run = response.data;

        // Start polling
        this.startPolling();
      } catch (error) {
        console.error("Failed to run flow:", error);
        throw error;
      }
    },
    async fetchFlowRunWithResults() {
      if (!this.flow_run) return;
      const flowRunId = this.flow_run.id;
      try {
        const response = await serverHttp.get(
          `/flow_runs/with_flow_result?id=${flowRunId}`,
        );
        this.flow_run = response.data;
      } catch (error) {
        console.error("Failed to fetch flow run:", error);
        throw error;
      }
    },
    async approveFlowRun() {
      if (!this.flow_run) return;
      try {
        const response = await serverHttp.put(
          `/flow_runs/approve?flow_run_id=${this.flow_run.id}`,
        );
        this.flow_run = response.data;
      } catch (error) {
        console.error("Failed to approve flow run:", error);
        throw error;
      }
    },
    setInnerInstructions(newValue) {
      if (!this.request) {
        this.request = { request_instructions: newValue };
      } else {
        this.request.request_instructions = newValue;
      }
    },
    async handleInstructionsUpdate(newValue) {
      if (newValue === this.previousRequestInstructions) {
        return;
      }

      try {
        if (!this.request.id) {
          await this.createRequest(newValue);
          return;
        }

        await this.updateRequest();
        if (!this.setOwnSummary) {
          await this.fetchSummary();
        }

        this.previousRequestInstructions = newValue;
      } catch (error) {
        this.request.request_instructions = this.previousRequestInstructions;
        throw error;
      }
    },
    clearFlow() {
      this.flow = null;
    },
    clearRequest() {
      this.request = {};
      this.previousRequestInstructions = "";
    },
    clearFlowRun() {
      this.flow_run = null;
    },
    clearAll() {
      this.clearFlow();
      this.clearRequest();
      this.clearFlowRun();
    },
    clearFlows() {
      this.flowsForTable.splice(0, this.flowsForTable.length);
    },
    setSetOwnSummary(value) {
      this.setOwnSummary = value;
    },
    setEditMode(value) {
      this.editMode = value;
    },
    startPolling() {
      if (this.isPolling) return;
      this.isPolling = true;
      this.pollingIntervalId = setInterval(async () => {
        await this.fetchFlowRunWithResults();
        if (
          ["completed", "failed", "canceled"].includes(this.flow_run.status)
        ) {
          this.stopPolling();
        }
      }, 1000); // Poll every 1 seconds
    },
    stopPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
      this.isPolling = false;
    },
  },
  getters: {
    requestInstructionsChanged: (state) => {
      return (
        state.request.request_instructions !== state.previousRequestInstructions
      );
    },
    getTaskOperationByIndex: (state) => (index) => {
      return state.flow?.task_operations.find((task) => task.index === index);
    },
    getNextTaskOperationIndex: (state) => {
      if (!state.flow || !state.flow.task_operations) return 0;

      const usedIndices = state.flow.task_operations.map((task) => task.index);

      let nextIndex = 0;
      while (usedIndices.includes(nextIndex)) {
        nextIndex++;
      }
      return nextIndex;
    },
    combinedTaskOperationsWithTaskRuns: (state) => {
      if (!state.flow) return [];
      if (!state.flow_run) return state.flow.task_operations;
      const taskOperations = state.flow.task_operations || [];
      const taskRuns = state.flow_run.task_runs || [];

      // Create a mapping from task_run.task_operation_index to task_run
      const taskRunMap = new Map();
      for (const taskRun of taskRuns) {
        taskRunMap.set(taskRun.task_operation_index, taskRun);
      }

      // Map task_operations to include their corresponding task_run
      const combined = taskOperations.map((taskOp) => {
        return {
          ...taskOp,
          task_run: taskRunMap.get(taskOp.index),
        };
      });
      return combined;
    },
    getInnerInstructions: (state) => {
      if (!state.request.request_instructions) return null;
      if (!state.request.request_instructions.includes("$$$$$$$$$$")) {
        return state.request.request_instructions;
      }
      return state.request.request_instructions.split("$$$$$$$$$$")[1];
    },
  },
});
